import { ReactNode, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, CircularProgress } from '@mui/material'
import { getCurrentNda, NDA_CURRENT_QUERY_KEY } from '@api/endpoints/nda'
import { useNdaStore } from '@store/ndaStore'
import { NdaAgreementScreen } from './NdaAgreementScreen'

interface NdaGateProps {
  children: ReactNode
}

/**
 * Stands in front of the authenticated app until the current NDA has been accepted.
 *
 * This mirrors, rather than replaces, the server's gate. The server refuses every authenticated
 * request from an account that has not signed, so an app rendered behind an un-accepted
 * agreement is not a partly-working app — it is a screen of failed requests. Asking once on load
 * turns that into the document itself.
 *
 * Three states, in order of how much we trust them:
 *
 * - `blocked` — the server said 403 `nda_required` on some other request. That is a fact about
 *   right now, so it wins over anything cached, and it is cleared only when a fresh fetch says
 *   we are through.
 * - a loaded status — the answer to "is there anything outstanding".
 * - nothing loaded yet — hold, but only briefly, and never on a failure. If the status cannot be
 *   fetched we let the app render: the server is still the real gate, and a network blip should
 *   not be indistinguishable from a locked account.
 */
export function NdaGate({ children }: NdaGateProps) {
  const blocked = useNdaStore(state => state.blocked)
  const clearBlocked = useNdaStore(state => state.clearBlocked)

  const { data, isPending, isFetching, isError, refetch } = useQuery({
    queryKey: NDA_CURRENT_QUERY_KEY,
    queryFn: getCurrentNda,
    // No stale window. This query is mounted once and refetched on mount, so the cost is one
    // request per app load — and the thing it guards against is reusing a cached "accepted" that
    // belonged to a different session or a superseded version.
    staleTime: 0,
    retry: 1,
  })

  // A 403 means whatever we hold is out of date — most often because a new version was published
  // while this session was open. Go and find out what it says.
  useEffect(() => {
    if (blocked) {
      refetch()
    }
  }, [blocked, refetch])

  // ...and stand down once the server itself says there is nothing outstanding. This is what
  // recovers from a 403 that raced an acceptance, without ever trusting a client-side "accepted".
  useEffect(() => {
    if (blocked && !isFetching && data && (!data.required || data.accepted)) {
      clearBlocked()
    }
  }, [blocked, isFetching, data, clearBlocked])

  const outstanding = !!data && data.required && !data.accepted

  if (blocked || outstanding) {
    return (
      <NdaAgreementScreen
        status={data}
        loading={isFetching}
        loadError={isError}
        onReload={() => {
          refetch()
        }}
      />
    )
  }

  // First load, before we know. Holding here avoids flashing an app whose every request is about
  // to be refused.
  if (isPending) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return <>{children}</>
}
